import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getRates } from ':fat/api/getRates';
import { CURRENCY_DATA, Network } from ':fat/core/networks';
import { getAddressesWithEthBalances, utxoBalancesByNetwork } from ':fat/database/queries';
import { query } from ':fat/messaging/entities/databaseWorker/actions';

export function useBalancesForFingerprint(fingerprint?: string) {
  const [balances, setBalances] = useState<Partial<Record<Network, bigint>>>({});
  const fiatBalances = useFiatBalances(balances);

  useEffect(() => {
    const poller = setInterval(() => void poll(), 100);

    async function poll() {
      if (self.SANITY_CHECK_COMPLETE && typeof fingerprint === 'string') {
        const [utxoBals, ethAddrs] = await Promise.all([
          query(utxoBalancesByNetwork(parseInt(fingerprint))),
          query(getAddressesWithEthBalances(parseInt(fingerprint))),
        ]);

        const newBalances = utxoBals.reduce(
          (allBals, bal) => ({ ...allBals, [bal.network]: bal.balance }),
          {} as Partial<Record<Network, bigint>>,
        );
        newBalances.eth = ethAddrs.reduce((total, a) => total + BigInt(a.eth_balance ?? '0'), 0n);
        setBalances(newBalances);
        clearInterval(poller);
      }
    }

    return () => {
      clearInterval(poller);
    };
  }, [fingerprint]);

  return { balances, fiatBalances };
}

export function useFiatBalances(balances: Partial<Record<Network, bigint>>) {
  const rates = useRates();

  if (!rates) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(balances).map(([network, bal]) => [
      network,
      convertToFiat(network as Network, bal, rates),
    ]),
  );
}

export function useFiatBalance(network: Network, balance: bigint) {
  const rates = useRates();

  if (!rates) {
    return 0;
  }

  return convertToFiat(network, balance, rates);
}

export function convertToFiat(network: Network, balance: bigint, rates: Record<Network, number>) {
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  if (!rates || !rates[network]) {
    return 0;
  }

  return (rates[network] ?? 0) * (Number(balance ?? 0n) / 10 ** CURRENCY_DATA[network].decimals);
}

export function useRates() {
  const { data: rates } = useQuery({ queryKey: ['rates'], queryFn: getRates, staleTime: 60000 });
  return rates;
}
